import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import Message from 'components/molecules/smsDashboard/message';
import InfiniteScroll from 'components/atoms/infinityScroll';
import ConversationEmptyIcon from 'components/atoms/icons/conversationEmptyIcon';
import MessagesSearchIcon from 'components/atoms/icons/messagesSearchEmptyIcon';
import { sleep } from 'utils';
import { getSmsDashboardMessagesKey } from 'infrastructure/hooks/smsDashboard/useMessages';
import {
  useQueryClient,
  type FetchNextPageOptions,
  type FetchPreviousPageOptions,
  type InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { DateFormats } from 'infrastructure/enums';
import { convertToDate } from 'infrastructure/functions';
import classNames from 'classnames';
import BaseLoader from 'components/atoms/base-loader';

import s from './styles.module.scss';

import type {
  IConversation,
  IMessage,
  ConversationType,
  IMessagesListRes,
  IMessagesListPages,
} from 'infrastructure/interfaces';
import type { RootState } from 'store';

interface IMessagesListProps {
  currentConversation: IConversation;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  hasNextPage?: boolean;
  isRefetching?: boolean;
  hasPreviousPage?: boolean;
  fetchPreviousPage: (
    options?: FetchPreviousPageOptions,
  ) => Promise<
    InfiniteQueryObserverResult<IMessagesListRes | undefined, unknown>
  >;
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<
    InfiniteQueryObserverResult<IMessagesListRes | undefined, unknown>
  >;
  messages?: (IMessagesListRes | undefined)[];
  searchValue: string;
  searchDateValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setSearchDateValue: React.Dispatch<React.SetStateAction<string>>;
  setIsSearchShown: React.Dispatch<React.SetStateAction<boolean>>;
  messagesLoading: boolean;
  scrollToMessage?: IMessage;
}

const messagesWrapperClass = s['messages-wrapper'];
export const scrollMessagesToBottom = () => {
  const el = document.querySelector(`.${messagesWrapperClass}`);
  if (!el) return;
  el.scrollTop = el.scrollHeight;
};

const MessagesList: React.FC<IMessagesListProps> = (props) => {
  const {
    currentConversation,
    setEnabled,
    messages,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    searchValue,
    searchDateValue,
    setSearchValue,
    setSearchDateValue,
    setIsSearchShown,
    messagesLoading,
    isRefetching = false,
    scrollToMessage,
  } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const containerRef = useRef(null);
  const { conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  useEffect(() => {
    scrollMessagesToBottom();
  }, [currentConversation]);

  const groupedByDate = useMemo(() => {
    if (!messages) return [];
    const dataByDate: { [key: string]: IMessage[] } = {};
    messages?.forEach((page) => {
      page?.items.forEach((item) => {
        if (item.createdAt) {
          const messageDate = new Date(item.createdAt);
          const formattedDate = format(messageDate, DateFormats['yyyy-MM-dd']);

          if (!dataByDate[formattedDate]) {
            dataByDate[formattedDate] = [];
          }

          dataByDate[formattedDate].push(item);
        }
      });
    });

    return Object.entries(
      Object.fromEntries(
        Object.entries(dataByDate).sort(([a], [b]) => b.localeCompare(a)),
      ),
    );
  }, [messages]);

  const formatDate = (dateToFormat: Date | string) => {
    const date = new Date(dateToFormat);
    if (isToday(date)) {
      return t('smsDashboard.today');
    }
    if (isYesterday(date)) {
      return t('smsDashboard.yesterday');
    }
    return format(date, DateFormats['dd MMMM, yy']);
  };

  const messageGroupDateTitle = (date: string) =>
    format(new Date(date), DateFormats['MMMM dd, yyyy']);

  useEffect(() => {
    if (scrollToMessage) {
      handleJumpToComment(scrollToMessage);
    }
  }, [currentConversation, scrollToMessage]);

  const handleJumpToComment = async (message: IMessage) => {
    setEnabled(false);
    await sleep(300);
    setSearchValue('');
    setSearchDateValue('');
    setIsSearchShown(false);

    const messagesKey = getSmsDashboardMessagesKey({
      conversationId: currentConversation.id,
      nextCursor: undefined,
      prevCursor: undefined,
      searchQuery: '',
      searchQueryDate: '',
    });
    queryClient.setQueryData<IMessagesListPages>(messagesKey, (oldData) => {
      if (!oldData) return;

      return {
        pageParams: oldData.pageParams,
        pages: [{ items: [message] }],
      };
    });

    setEnabled(true);

    await sleep(500);

    await fetchPreviousPage({
      pageParam: {
        prevCursor: message.createdAt,
        searchQuery: '',
      },
    });

    await fetchNextPage({
      pageParam: {
        nextCursor: message.createdAt,
        searchQuery: '',
      },
    });
    await sleep(500);

    const currentMessageEl = document.getElementById(`${message.createdAt}`);
    if (currentMessageEl) {
      currentMessageEl.scrollIntoView({ behavior: 'instant', block: 'center' });
    }
  };

  const findClosestDate = (
    dates: (Date | string | number)[],
    targetDate: Date | string | number,
  ): Date | null => {
    if (!dates.length) return null;
    const targetTime = new Date(targetDate).getTime();
    return (
      dates.reduce<{ date: Date; diff: number } | null>((closest, current) => {
        const currentDate = new Date(current);
        const currentDiff = Math.abs(currentDate.getTime() - targetTime);
        if (!closest || currentDiff < closest.diff) {
          return { date: currentDate, diff: currentDiff };
        }
        return closest;
      }, null)?.date ?? null
    );
  };

  const fetchPreviousPageMessages = useCallback(async () => {
    const mess = await fetchPreviousPage();
    if (
      mess.data?.pages[0] &&
      mess.data?.pages[0].items.length < 15 &&
      currentConversation.id
    ) {
      await fetchNextPage();
    }
  }, [groupedByDate]);

  useEffect(() => {
    if (searchDateValue !== '' && !messagesLoading) {
      const date = parseISO(searchDateValue);
      const nearDate = findClosestDate(
        groupedByDate.map((item) =>
          convertToDate(item[0], DateFormats['yyyy-MM-dd']),
        ),
        date,
      );
      if (!nearDate) return;

      const id = `${format(nearDate, DateFormats['yyyy-MM-dd'])}`;

      const dateGroup = document.getElementById(id);
      if (dateGroup) {
        dateGroup.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        });
      } else {
        const dateMonthGroup = document.querySelector(
          `[id^="${format(nearDate, DateFormats['yyyy-MM'])}"]`,
        );
        if (dateMonthGroup) {
          dateMonthGroup.scrollIntoView({
            behavior: 'instant',
            block: 'start',
          });
        }
      }
    }
  }, [searchDateValue, groupedByDate, messagesLoading]);

  const messagesWrapperClassName = classNames(messagesWrapperClass, {
    [s['loader-opacity']]: isRefetching,
  });

  return (
    <>
      {isRefetching && (
        <div className={s.loader}>
          <BaseLoader loading width={20} height={20} relative />
        </div>
      )}
      <div className={messagesWrapperClassName} ref={containerRef}>
        <InfiniteScroll
          loadMore={fetchPreviousPageMessages}
          disabled={!hasPreviousPage || messagesLoading}
          allLoaded={!hasPreviousPage}
          uniqueId="messages-prev"
        />
        {groupedByDate.map((group) => (
          <div className={s['message-group']} key={group[0]} id={group[0]}>
            <div
              className={s['message-group-date']}
              title={messageGroupDateTitle(group[0])}
            >
              {formatDate(group[0])}
            </div>
            {(group[1] as IMessage[])
              .reverse()
              .filter((message: any, index: number, self: any) => {
                return (
                  self.findIndex(
                    (otherMessage: any) => otherMessage.id === message.id,
                  ) === index
                );
              })
              .map((message: IMessage, index: number) => {
                const {
                  body,
                  authorType,
                  status,
                  createdAt,
                  from,
                  to,
                  action,
                  author,
                  mediaUrl,
                  readBy,
                  readAt,
                } = message;
                const authorName =
                  authorType === 'patient'
                    ? currentConversation?.patient?.fullNameWithOnlyTitle
                    : author?.fullNameWithProfession;
                return (
                  <Message
                    left={authorType === 'patient'}
                    body={body}
                    action={action}
                    authorType={authorType}
                    authorName={authorName}
                    status={status}
                    createdAt={createdAt}
                    to={to}
                    from={from}
                    conversationType={conversationType as ConversationType}
                    key={index}
                    mediaUrl={mediaUrl}
                    readBy={readBy}
                    readAt={readAt}
                    handleJumpToComment={() => handleJumpToComment(message)}
                    isShowJumpToMsgBtn={Boolean(searchValue)}
                    searchValue={searchValue}
                    scrollToMessage={scrollToMessage}
                  />
                );
              })}
          </div>
        ))}
        {!groupedByDate?.length && messagesLoading === false && (
          <div className={s['empty-conversation']} style={{ height: '100%' }}>
            <div className={s['message-wrapper']}>
              {searchValue === undefined ? (
                <div className={s['empty-message']}>
                  <ConversationEmptyIcon />
                  <div className={s.message}>
                    {t('smsDashboard.conversation_is_empty')}
                  </div>
                </div>
              ) : (
                <div className={`${s['empty-message']} ${s['search-empty']}`}>
                  <MessagesSearchIcon />
                  <div className={s.message} style={{ width: 300 }}>
                    <Trans
                      i18nKey="smsDashboard.empty_conversation_message2"
                      values={{
                        description1: `${t(
                          'smsDashboard.no_result_found',
                        )},<br/>`,
                        description2: t('smsDashboard.change_filter'),
                      }}
                      components={{
                        primary: <span />,
                        secondary: <span />,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <InfiniteScroll
          loadMore={fetchNextPage}
          disabled={!hasNextPage || messagesLoading}
          allLoaded={!hasNextPage}
          uniqueId="messages-next"
        />
      </div>
    </>
  );
};

export default MessagesList;
